import { useCallback, useMemo } from 'react';
import AttributesTable from 'src/assets/static/view_checkbook/AttributesTable';
import Button from 'src/components/design-system/Button';
import useBoolean from 'src/hooks/useBoolean';

import { toast } from 'react-toastify';

import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import { DefaultTransferListDataType } from 'src/components/TransferList';
import useConfirm from 'src/hooks/useConfirm';
import usePeriodView from 'src/hooks/usePeriodView';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import { ApprovalStatus } from 'src/services/useGlCodePeriodBudgetApi';
import useUpdateGLCodeAttributesApi from 'src/services/useUpdateGLCodeAttributesApi';
import { transformData } from '../../ViewAllAttributeUserAssignments';

const resetUsers = (users: DefaultTransferListDataType[]) =>
  [...users].map((user) => ({ ...user, isDirty: false, selected: false }));

const ManageGLCodeAttributeAction = () => {
  const { refreshGLCodePeriodTable, setGlCodePeriodBudget, isGlCodesBudgetLoading, clients } =
    useViewCheckbook();
  const {
    t,
    isBulkManageGLCode,
    selectedGLCode,
    setSelectedGLCode,
    poAttributesOption,
    invoiceAttributesOption,
    poAssignedUsers,
    invoiceAssignedUsers,
    isManageGLCodeAttributesDirty,
    handleCancelSidePanel,
    setInvoiceAssignedUsers,
    setInvoiceUnassignedUsers,
    setPoAssignedUsers,
    setPoUnassignedUsers,
    refreshBulkAssign,
    isSelectedGLCodeEmpty,
    setOriginAttributeOptions,
    setInitialSelectedGLCode,
  } = usePeriodView();
  const { updateGLCodeAttributes, isUpdatingGLCodeAttributes } = useUpdateGLCodeAttributesApi();
  const { confirm } = useConfirm();

  const { value: openAssignmentsModal, toggle: toggleAssignmentsModal } = useBoolean();

  const attributesData = selectedGLCode.flatMap((row) => transformData(row, clients));

  const { saveMessage, isSaveDisabled, isAssignedUser } = useMemo(() => {
    const isAssignedUser = !(
      (poAttributesOption !== ApprovalStatus[0] && poAssignedUsers.length === 0) ||
      (invoiceAttributesOption !== ApprovalStatus[0] && invoiceAssignedUsers.length === 0)
    );

    const isSaveDisabled =
      isSelectedGLCodeEmpty || !isAssignedUser || !isManageGLCodeAttributesDirty;
    let message: string[] = [];

    if (isSelectedGLCodeEmpty) {
      message = ['Please select GL Code(s)'];
    } else if (!isAssignedUser) {
      message = ['Please assign at least one contact to active attributes or set it to off.'];
    }
    return {
      isSaveDisabled,
      saveMessage: message,
      isAssignedUser,
    };
  }, [
    invoiceAssignedUsers.length,
    invoiceAttributesOption,
    isManageGLCodeAttributesDirty,
    isSelectedGLCodeEmpty,
    poAssignedUsers.length,
    poAttributesOption,
  ]);

  const saveGLCodeAttributes = useCallback(async () => {
    const glCodeIds = selectedGLCode.map((item) => item.id);
    const poApprovalStatus = ApprovalStatus.indexOf(poAttributesOption);
    const invoiceApprovalStatus = ApprovalStatus.indexOf(invoiceAttributesOption);
    const poApprovalUsers = poApprovalStatus === 0 ? [] : poAssignedUsers.map((user) => user.id);
    const invoiceApprovalUsers =
      invoiceApprovalStatus === 0 ? [] : invoiceAssignedUsers.map((user) => user.id);
    await updateGLCodeAttributes({
      glCodeIds,
      poApprovalStatus,
      poApprovalUsers,
      invoiceApprovalStatus,
      invoiceApprovalUsers,
    }).then(async () => {
      toast.success('GL Code Attributes updated successfully');
      setInitialSelectedGLCode(selectedGLCode);
      setPoAssignedUsers(resetUsers);
      setInvoiceAssignedUsers(resetUsers);
      setPoUnassignedUsers(resetUsers);
      setInvoiceUnassignedUsers(resetUsers);
      setOriginAttributeOptions({
        poAttribute: poAttributesOption,
        invoiceAttribute: invoiceAttributesOption,
      });
      refreshBulkAssign();
      await refreshGLCodePeriodTable();
    });
  }, [
    invoiceAssignedUsers,
    invoiceAttributesOption,
    poAssignedUsers,
    poAttributesOption,
    refreshBulkAssign,
    refreshGLCodePeriodTable,
    selectedGLCode,
    setInitialSelectedGLCode,
    setInvoiceAssignedUsers,
    setInvoiceUnassignedUsers,
    setOriginAttributeOptions,
    setPoAssignedUsers,
    setPoUnassignedUsers,
    updateGLCodeAttributes,
  ]);

  const handleBulkGLCodeAttribute = useCallback(async () => {
    await confirm({
      title: 'Save Attributes',
      content:
        'The previously set attributes and assignments for these GL Code(s) will be overwritten by saving these changes. Please confirm to proceed.',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'OK',
      width: '500px',
    }).then(async (isAccepted) => {
      if (isAccepted) {
        await saveGLCodeAttributes();
        setTimeout(() => {
          const GLCodeIds = selectedGLCode.map((item) => item.id);
          setGlCodePeriodBudget((prev) => {
            const glCodes = prev.map((item) => ({
              ...item,
              selected: GLCodeIds.includes(item?.id),
            }));
            const selectedGLCodes = glCodes.filter((item) => item.selected);
            setSelectedGLCode(selectedGLCodes);
            return glCodes;
          });
        }, 500);
      }
    });
  }, [confirm, saveGLCodeAttributes, selectedGLCode, setGlCodePeriodBudget, setSelectedGLCode]);

  const { isDefaultBulkState, isDefaultBulkStateDirty } = useMemo(() => {
    const isDefaultBulkState =
      poAttributesOption === ApprovalStatus[0] && invoiceAttributesOption === ApprovalStatus[0];
    const isDefaultBulkStateDirty = selectedGLCode.some(
      (item) => item.poApproval !== ApprovalStatus[0] || item.invoiceApproval !== ApprovalStatus[0]
    );
    return { isDefaultBulkState, isDefaultBulkStateDirty };
  }, [invoiceAttributesOption, poAttributesOption, selectedGLCode]);

  const handleSave = useCallback(async () => {
    if (isBulkManageGLCode) {
      if (isDefaultBulkState) {
        if (isDefaultBulkStateDirty) {
          handleBulkGLCodeAttribute();
        } else {
          handleCancelSidePanel();
        }
      } else if (!isManageGLCodeAttributesDirty) {
        handleCancelSidePanel();
      } else {
        handleBulkGLCodeAttribute();
      }
    } else {
      if (!isManageGLCodeAttributesDirty) {
        handleCancelSidePanel();
        return;
      }
      await saveGLCodeAttributes();
    }
  }, [
    handleBulkGLCodeAttribute,
    handleCancelSidePanel,
    isBulkManageGLCode,
    isDefaultBulkState,
    isDefaultBulkStateDirty,
    isManageGLCodeAttributesDirty,
    saveGLCodeAttributes,
  ]);

  return (
    <section>
      <div className="relative ">
        <div className="absolute">
          <p className="font-bold text-md my-2">
            {isBulkManageGLCode
              ? t('Bulk Manage GL Code(s) Attributes')
              : t('Manage GL Code Attributes')}
          </p>
        </div>
        <div className="flex items-center justify-center space-x-2 my-2">
          {isBulkManageGLCode && (
            <Button size={'md'} color="secondary" onClick={toggleAssignmentsModal}>
              {t('View Assignments for Selected')}
            </Button>
          )}
          <PopUp
            popupAlign={{ horizontal: 'center', vertical: 'top' }}
            content={
              <div className="m-1 max-w-[350px]">
                <PopUpContentWrapper type={'Error'} contents={saveMessage} />
              </div>
            }
            show={isSelectedGLCodeEmpty || !isAssignedUser}
          >
            <Button
              size={'md'}
              disabled={isSaveDisabled}
              loading={isUpdatingGLCodeAttributes || isGlCodesBudgetLoading}
              onClick={handleSave}
            >
              {t('Save')}
            </Button>
          </PopUp>
          <Button
            onClick={handleCancelSidePanel}
            size={'md'}
            color={'info'}
            disabled={isUpdatingGLCodeAttributes || isGlCodesBudgetLoading}
          >
            {t('Cancel')}
          </Button>
        </div>
      </div>
      <AttributesTable
        openAssignmentsModal={openAssignmentsModal}
        toggleAssignmentsModal={toggleAssignmentsModal}
        GLCodeAttributesData={attributesData}
        title="View GL Code Attributes and Assignments"
      />
    </section>
  );
};
export default ManageGLCodeAttributeAction;
